import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";

interface AdminProfile {
  id: string;
  full_name: string;
  languages: string[];
  occupation: string;
  workplace: string;
  fos_groups_count: number;
}

interface QRCodeStats {
  [userId: string]: {
    totalQrCodes: number;
    qrCodesWithAnswers: number;
  };
}

export const GroupLeaderOverview = () => {
  const { data: profiles, isLoading: profilesLoading } = useQuery({
    queryKey: ["admin-profiles-overview"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("admin_profiles")
        .select("id, full_name, languages, occupation, workplace, fos_groups_count")
        .order("full_name");

      if (error) throw error;
      return data as AdminProfile[];
    },
  });

  const { data: qrCodeStats, isLoading: statsLoading } = useQuery({
    queryKey: ["qr-code-stats-by-leader"],
    queryFn: async () => {
      // Get all auth_tokens with their created_by
      const { data: tokens, error: tokensError } = await supabase
        .from("auth_tokens")
        .select("token, created_by");

      if (tokensError) throw tokensError;

      // Get all survey responses to know which tokens have answers
      const { data: responses, error: responsesError } = await supabase
        .from("survey_responses")
        .select("auth_token");

      if (responsesError) throw responsesError;

      // Create a set of tokens that have at least one response
      const tokensWithResponses = new Set(responses?.map(r => r.auth_token) || []);

      // Aggregate stats by created_by (group leader id)
      const stats: QRCodeStats = {};
      
      tokens?.forEach(token => {
        const userId = token.created_by;
        if (!userId) return;

        if (!stats[userId]) {
          stats[userId] = { totalQrCodes: 0, qrCodesWithAnswers: 0 };
        }
        
        stats[userId].totalQrCodes++;
        if (tokensWithResponses.has(token.token)) {
          stats[userId].qrCodesWithAnswers++;
        }
      });

      return stats;
    },
  });

  const isLoading = profilesLoading || statsLoading;

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-64" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-32 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Registrerade gruppledare</CardTitle>
        <CardDescription>
          Översikt över gruppledare och deras registrerade information
        </CardDescription>
      </CardHeader>
      <CardContent>
        {profiles && profiles.length > 0 ? (
          <div className="rounded-md border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Namn</TableHead>
                  <TableHead>Språk</TableHead>
                  <TableHead>Yrke</TableHead>
                  <TableHead>Arbetsplats</TableHead>
                  <TableHead className="text-center">Antal FÖS-grupper</TableHead>
                  <TableHead className="text-center">Genererade QR-koder</TableHead>
                  <TableHead className="text-center">QR-koder med svar</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {profiles.map((profile) => {
                  const stats = qrCodeStats?.[profile.id] || { totalQrCodes: 0, qrCodesWithAnswers: 0 };
                  return (
                    <TableRow key={profile.id}>
                      <TableCell className="font-medium">{profile.full_name}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {profile.languages && profile.languages.length > 0 ? (
                            profile.languages.map((lang) => (
                              <Badge key={lang} variant="secondary" className="text-xs">
                                {lang}
                              </Badge>
                            ))
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{profile.occupation || "-"}</TableCell>
                      <TableCell>{profile.workplace || "-"}</TableCell>
                      <TableCell className="text-center">
                        <Badge variant={profile.fos_groups_count > 0 ? "default" : "outline"}>
                          {profile.fos_groups_count}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant={stats.totalQrCodes > 0 ? "secondary" : "outline"}>
                          {stats.totalQrCodes}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant={stats.qrCodesWithAnswers > 0 ? "default" : "outline"}>
                          {stats.qrCodesWithAnswers}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground text-center py-8">
            Inga gruppledare har registrerat sig ännu.
          </p>
        )}
      </CardContent>
    </Card>
  );
};
